import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { REQUEST_PASSWORD_RESET_MUTATION } from '../graphql/mutations';
import { AuthCard } from '../components/AuthCard';

/** Forgot password page: request a password reset email. */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [requestReset, { loading }] = useMutation(REQUEST_PASSWORD_RESET_MUTATION);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      await requestReset({ variables: { email } });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    }
  };

  if (submitted) {
    return (
      <AuthCard title="Check your email" subtitle="Reset link on its way">
        <div className="space-y-6 text-center">
          <p className="text-[var(--text-secondary)]">
            If an account exists for {email}, you will receive a password reset link shortly.
          </p>
          <Link to="/login" className="btn btn-primary inline-block">
            Back to sign in
          </Link>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Reset your password" subtitle="We'll email you a reset link">
      <form className="space-y-6" onSubmit={handleSubmit}>
        <p className="text-[var(--text-secondary)]">
          Enter your email address and we'll send you a link to reset your password.
        </p>

        {error && (
          <div className="bg-[color-mix(in_oklab,var(--coral-400)_10%,transparent)] border border-[color-mix(in_oklab,var(--coral-400)_35%,transparent)] text-[var(--text-primary)] px-4 py-3 rounded-xl">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="email" className="label">
            Email address
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1"
            placeholder="you@example.com"
          />
        </div>

        <button type="submit" disabled={loading} className="btn btn-primary w-full py-3">
          {loading ? 'Sending…' : 'Send reset link'}
        </button>

        <div className="text-center">
          <Link
            to="/login"
            className="text-sm text-[var(--primary)] hover:text-[var(--primary-hover)]"
          >
            Back to sign in
          </Link>
        </div>
      </form>
    </AuthCard>
  );
}
